import { TradeoffTypeEnum } from "@clockwise/schema";
import { EventThread } from "@clockwise/web-commons/src/ui/event-thread/";
import { isEmpty } from "lodash";
import React from "react";
import { RENDERED_IN } from "../../../../constants";
import { proposalIsScheduleByMoving } from "../../../../util/proposalIsScheduleByMoving";
import { GQLAssistantMessageProposalResponse } from "../../../utils/types";
import { ProposalDiffBlocks } from "../../diffs/ProposalDiffBlocks";
import { ConsequencesEvents } from "./ConsequencesEvents";
import { ThreadItemHeader } from "./ThreadItemHeader";
import { TradeoffEvents } from "./TradeoffEvents";
import { TradeoffsSummary } from "./TradeoffsSummary";
import {
  getConsequencesNamesForHeader,
  getTradeoffNamesForHeader,
  removeTradeoffsEventsAlsoInConsequences,
} from "./util/proposal-thread.util";

export const ProposalThread = ({
  proposal,
  variant = RENDERED_IN.AI_CHAT,
  viewerIsProposalOwner = true,
}: {
  proposal: GQLAssistantMessageProposalResponse["proposal"];
  variant?: string;
  viewerIsProposalOwner?: boolean;
}) => {
  const { consequencesBlock, diffBlocks } = proposal;
  const yourCalDiffs = consequencesBlock?.yourCalDiffs || [];
  const otherCalDiffs = consequencesBlock?.otherCalDiffs || [];
  const activeDiff = diffBlocks.find((diffBlock) => diffBlock.diffs.find((diff) => diff.active));
  const hasConsequences = !isEmpty(yourCalDiffs) || !isEmpty(otherCalDiffs);
  const isScheduleByMoving = proposalIsScheduleByMoving(proposal);

  const tradeoffBlocks = activeDiff?.diffs.map((diff) => diff.tradeoffBlocks).flat() || [];

  const tradeoffThreadItemsAll = tradeoffBlocks.filter(
    (tradeoffBlock) =>
      ![TradeoffTypeEnum.OUT_OF_TIME_RANGE, TradeoffTypeEnum.INCONVENIENCE].includes(
        tradeoffBlock.tradeoffType,
      ),
  );

  const tradeoffThreadItems = removeTradeoffsEventsAlsoInConsequences({
    tradeoffThreadItemsAll,
    yourCalDiffs,
    otherCalDiffs,
  });
  const hasTradeoffThreadItems = tradeoffThreadItems.length > 0;
  const namesOfTradeoffs = getTradeoffNamesForHeader(tradeoffThreadItems);
  const namesOfConsequences = getConsequencesNamesForHeader(consequencesBlock || null);

  const renderedInSharedProposal = variant === RENDERED_IN.SHARED_PROPOSAL;

  return (
    <div cw-id="proposal-response" className="cw-flex-col cw-w-full">
      <EventThread>
        <EventThread.Source>
          <ProposalDiffBlocks
            diffBlocks={diffBlocks}
            isScheduleByMoving={isScheduleByMoving}
            tradeoffTypes={[
              TradeoffTypeEnum.OUT_OF_TIME_RANGE,
              TradeoffTypeEnum.INCONVENIENCE,
              TradeoffTypeEnum.LOW_VIZ_INCONVENIENCE,
            ]}
            options={proposal.options.__typename === "SchedulingOptions" ? proposal.options : null}
            variant={variant}
            viewerIsProposalOwner={viewerIsProposalOwner}
          />
        </EventThread.Source>
        {hasConsequences && (
          <>
            {(!renderedInSharedProposal ||
              (renderedInSharedProposal && !isEmpty(yourCalDiffs))) && (
              <EventThread.Break type="sbm">
                <ThreadItemHeader attendeesInfo={namesOfConsequences} type="sbm" />
              </EventThread.Break>
            )}
            <ConsequencesEvents
              diffs={yourCalDiffs}
              isScheduleByMoving={isScheduleByMoving}
              viewerIsProposalOwner={viewerIsProposalOwner}
              variant={variant}
            />
            <ConsequencesEvents diffs={otherCalDiffs} isScheduleByMoving={isScheduleByMoving} />
          </>
        )}
        {hasTradeoffThreadItems && (
          <>
            <EventThread.Break type="unavailable">
              <ThreadItemHeader attendeesInfo={namesOfTradeoffs} type="unavailable" />
            </EventThread.Break>
            <TradeoffEvents tradeoffs={tradeoffThreadItems} />
            <TradeoffsSummary tradeoffs={tradeoffThreadItems} />
          </>
        )}
      </EventThread>
    </div>
  );
};
